import { AuthError } from '@aws-amplify/auth'
import { cognitoUserPoolsTokenProvider } from '@aws-amplify/auth/cognito'
import { Primitive } from '@sentry/core'
import { withScope } from '@sentry/nextjs'

import { getValidTokenFailed } from '@/constants/sentry-type'
import { SECOND } from '@/constants/unit'

import { sleep } from './sleep'

let getOrRefreshTokensPromise: ReturnType<typeof cognitoUserPoolsTokenProvider.getTokens> | undefined

export interface Token {
  idToken?: string
  expiration?: number
}

const MAX_RETRY_COUNT = 2

export async function getValidToken(retryCount: number = 0): Promise<Token> {
  const tags: Record<string, Primitive> = { type: getValidTokenFailed, retryCount }
  try {
    if (!getOrRefreshTokensPromise) {
      const localTokens = await cognitoUserPoolsTokenProvider.tokenOrchestrator.getTokenStore().loadTokens()
      const exp = localTokens?.idToken?.payload.exp
      const forceRefresh = exp !== undefined && Date.now() + (20 * SECOND) > exp * SECOND
      getOrRefreshTokensPromise = cognitoUserPoolsTokenProvider.getTokens({
        forceRefresh,
      })
      tags.exp = exp
      tags.forceRefresh = forceRefresh
    }
    const tokens = await getOrRefreshTokensPromise
    getOrRefreshTokensPromise = undefined
    const idToken = tokens?.idToken?.toString()
    const expiration = tokens?.idToken?.payload.exp
    tags.idToken = idToken
    return { idToken, expiration }
  } catch (e) {
    getOrRefreshTokensPromise = undefined
    if (retryCount < MAX_RETRY_COUNT) {
      await sleep(3 * SECOND)
      return await getValidToken(retryCount + 1)
    }
    withScope((scope) => {
      scope.setTags(tags)
      if (e instanceof AuthError) {
        scope.captureException(e.underlyingError)
      } else {
        scope.captureException(e)
      }
    })
    return {}
  }
}
